'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

import { Anchor, Box, Flex, Group, Text, VisuallyHidden } from '@mantine/core';

import { Block } from '@marketing/components/blocks/Block';
import { TextBlock } from '@marketing/components/blocks/TextBlock';
import { PageHeader } from '@marketing/components/page-header/PageHeader';
import { ResourceProFooterBlock } from '@marketing/components/resourcepro-blocks/ResourceProFooterBlock';
import { Section } from '@marketing/components/section/Section';
import { SectionHeader } from '@marketing/components/section/SectionHeader';

import { ReplusLogo } from 'components/logo/ReplusLogo';
import { SEIALogo } from 'components/logo/SEIALogo';
import { SEPALogo } from 'components/logo/SEPALogo';

import firstEditionImage from './editions/RE+Source PRO Insider - First Edition.png';
import secondEditionImage from './editions/RE+Source PRO Insider - Second Edition.png';

const EDITIONS = [
    { url: '/uploads/insider_1.pdf', image: firstEditionImage, title: 'First Edition' },
    { url: '/uploads/insider_2.pdf', image: secondEditionImage, title: 'Second Edition' },
];

const InsiderPage = () => {
    return (
        <>
            <PageHeader
                color="white"
                bg="purple"
                variant="dark"
                title={
                    <>
                        <ReplusLogo c="white" w={120} />
                        <VisuallyHidden>RE+Source PRO Insider</VisuallyHidden>
                    </>
                }
                subtitle="RE+Source PRO Insider"
                description="Tips and updates for RE+Source PRO providers"
                actions={
                    <Group>
                        <Flex fw={500} opacity={0.6} fz="xs" ta="right" pt={-6}>
                            Powered by
                        </Flex>
                        <Link href="https://seia.org/" target="_blank" rel="noopener noreferrer">
                            <SEIALogo isWhite height={50} />
                        </Link>
                        <Link href="https://sepapower.org/" target="_blank" rel="noopener noreferrer">
                            <SEPALogo isWhite height={50} />
                        </Link>
                    </Group>
                }
            />

            <Section>
                <SectionHeader title="About RE+Source PRO Insider" description=" " />

                <Block size="md">
                    <TextBlock>
                        <Box>
                            RE+Source PRO Insider is a quick read newsletter aimed at providing RE+Source PRO product
                            and service providers information on how to leverage the platform to promote sales and
                            partnerships. Each edition contains useful tips like how to generate leads, how to use QR
                            codes when exhibiting at events, how to share your profile on social media, etc. In
                            addition, when new features are added to RE+Source PRO, you'll be the first to hear about
                            them and how to utilize them to drive your business.
                        </Box>
                    </TextBlock>
                </Block>
            </Section>

            <Section variant="light">
                <SectionHeader title="How to Receive" description=" " />

                <Block size="md">
                    <TextBlock>
                        <Box>
                            Product and service providers who have claimed their company profile will receive RE+Source
                            PRO Insider by email from Direct Energy Partners, the platform's host. We encourage you to
                            pass it along to team members who would benefit as well.
                        </Box>
                    </TextBlock>
                </Block>
            </Section>

            <Section>
                <SectionHeader title="RE+Source PRO Insider" description=" " />

                <Block size="md">
                    <Group gap="xl" justify="center" mt="md">
                        {EDITIONS.map((edition) => (
                            <Box key={edition.title} style={{ width: '33%', textAlign: 'center' }}>
                                <Anchor href={edition.url} target="_blank" rel="noopener noreferrer">
                                    <Image
                                        src={edition.image}
                                        alt={edition.title}
                                        style={{
                                            width: '100%',
                                            height: '300px',
                                            objectFit: 'contain',
                                            overflow: 'hidden',
                                            border: '1px solid var(--mantine-color-gray-2)',
                                            borderRadius: 'var(--mantine-radius-md)',
                                            boxShadow: 'var(--mantine-shadow-md)',
                                        }}
                                    />
                                </Anchor>
                                <Text mt="sm" fw={500}>
                                    {edition.title}
                                </Text>
                            </Box>
                        ))}
                    </Group>
                </Block>
            </Section>

            <ResourceProFooterBlock pricing={false} exhibit />
        </>
    );
};

export default InsiderPage;
